import { useState, useEffect } from "react";
import {
  Save, RotateCcw, Search, Plus, Trash2, BookOpen, Dumbbell
} from "lucide-react";
import { API, SCHEMA_OPTIONS, DOEL_LABELS, DOEL_COLORS, SPIERGROEPEN_LIB } from "./adminConstants";
import { getSchema } from "../../data/schemaData";
import { HYROX_TRAININGEN } from "../../data/schemaDataHyrox";

function ExerciseLibraryPickerModal({ onSelect, onClose }) {
  const [exercises, setExercises] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    fetch(`${API}/api/exercises`).then(r => r.json()).then(d => setExercises(d.exercises || []));
  }, []);

  const filtered = exercises.filter(e =>
    (!query || e.naam.toLowerCase().includes(query.toLowerCase())) &&
    (!filter || e.spiergroep === filter)
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg flex flex-col overflow-hidden" style={{ maxHeight: "75vh" }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <h3 className="text-white font-bold text-sm flex items-center gap-2"><BookOpen size={14} className="text-[#C9A84C]" /> Uit bibliotheek kiezen</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">✕</button>
        </div>
        <div className="px-4 pt-3 pb-2 space-y-2">
          <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2">
            <Search size={13} className="text-zinc-500 shrink-0" />
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Zoek oefening..." className="bg-transparent flex-1 text-sm text-white outline-none placeholder-zinc-600" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilter("")} className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-colors ${!filter ? "bg-[#C9A84C] text-black" : "bg-[#1a1a1a] text-zinc-400 hover:text-white"}`}>Alles</button>
            {SPIERGROEPEN_LIB.map(s => (
              <button key={s} onClick={() => setFilter(filter === s ? "" : s)}
                className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-colors ${filter === s ? "bg-[#C9A84C] text-black" : "bg-[#1a1a1a] text-zinc-400 hover:text-white"}`}>{s}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {filtered.map((ex, i) => (
            <button key={i} onClick={() => { onSelect(ex); onClose(); }}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-[#1a1a1a] transition-colors text-left group">
              <div>
                <span className="text-white text-sm font-medium group-hover:text-[#C9A84C] transition-colors block">{ex.naam}</span>
                {(ex.apparaat || ex.type) && (
                  <span className="text-zinc-600 text-xs">{[ex.type, ex.apparaat].filter(Boolean).join(" · ")}</span>
                )}
              </div>
              <span className="text-zinc-600 text-xs shrink-0 ml-2">{ex.spiergroep}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-zinc-600 text-sm text-center py-6">Geen oefeningen gevonden</p>}
        </div>
      </div>
    </div>
  );
}

function ExerciseEditor({ ex, idx, onChange, onDelete }) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <div className="bg-[#141414] border border-white/8 rounded-xl p-4 space-y-3">
      {showPicker && (
        <ExerciseLibraryPickerModal
          onSelect={picked => onChange({ ...ex, naam: picked.naam, notitie: picked.uitvoeringstip || ex.notitie })}
          onClose={() => setShowPicker(false)}
        />
      )}
      <div className="flex items-center justify-between mb-1">
        <span className="text-[#C9A84C] text-xs font-bold font-mono bg-[#C9A84C]/10 px-2 py-0.5 rounded">
          {ex.code || `${idx + 1}`}
        </span>
        <button onClick={onDelete} title="Verwijder oefening" className="text-zinc-600 hover:text-red-400 transition-colors p-1">
          <Trash2 size={13} />
        </button>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Oefening naam</label>
          <button onClick={() => setShowPicker(true)} className="flex items-center gap-1 text-[#C9A84C] text-xs hover:underline">
            <BookOpen size={11} /> Uit bibliotheek
          </button>
        </div>
        <input value={ex.naam} onChange={e => onChange({ ...ex, naam: e.target.value })}
          className="course-input text-sm" placeholder="bijv. Squat – Barbell" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1">Sets/Reps</label>
          <input value={ex.sets_reps} onChange={e => onChange({ ...ex, sets_reps: e.target.value })}
            className="course-input text-sm font-mono" placeholder="3 x 12" />
        </div>
        <div>
          <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1">Tempo</label>
          <input value={ex.tempo} onChange={e => onChange({ ...ex, tempo: e.target.value })}
            className="course-input text-sm font-mono" placeholder="3010" />
        </div>
        <div>
          <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1">Rust</label>
          <input value={ex.rust} onChange={e => onChange({ ...ex, rust: e.target.value })}
            className="course-input text-sm font-mono" placeholder="90 sec" />
        </div>
      </div>
      <div>
        <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1">Uitvoeringstip</label>
        <textarea value={ex.notitie || ""} onChange={e => onChange({ ...ex, notitie: e.target.value })}
          rows={2} className="course-input text-sm resize-none" placeholder="Uitvoeringstip voor de cursist..." />
      </div>
    </div>
  );
}

function SessionEditor({ session, onChange, token }) {
  const updateExercise = (idx, updated) => {
    const exs = [...session.exercises];
    exs[idx] = updated;
    onChange({ ...session, exercises: exs });
  };
  const deleteExercise = (idx) => {
    onChange({ ...session, exercises: session.exercises.filter((_, i) => i !== idx) });
  };
  const addExercise = () => {
    const code = String.fromCharCode(65 + session.exercises.length) + "1";
    onChange({
      ...session,
      exercises: [...session.exercises, { code, naam: "", sets_reps: "3 x 10", tempo: "2010", rust: "60 sec", superset: null, notitie: "" }]
    });
  };

  return (
    <div className="space-y-3">
      <div className="mb-3">
        <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1">Sessie naam</label>
        <input value={session.naam} onChange={e => onChange({ ...session, naam: e.target.value })}
          className="course-input text-sm max-w-xs" placeholder="bijv. Sessie A – Push" />
      </div>
      {session.exercises.map((ex, i) => (
        <ExerciseEditor key={ex.code || `ex-${i}`} ex={ex} idx={i} token={token}
          onChange={upd => updateExercise(i, upd)}
          onDelete={() => deleteExercise(i)} />
      ))}
      <button onClick={addExercise}
        className="w-full border border-dashed border-white/15 rounded-xl py-3 text-zinc-500 hover:text-[#C9A84C] hover:border-[#C9A84C]/30 transition-colors text-sm flex items-center justify-center gap-2">
        <Plus size={14} /> Oefening toevoegen
      </button>
    </div>
  );
}

export default function SchemaEditorView({ api, showToast, token }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const [editSessions, setEditSessions] = useState(null);
  const [editWeek2, setEditWeek2] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasOverride, setHasOverride] = useState(false);
  const [search, setSearch] = useState("");
  const [dirty, setDirty] = useState(false);

  const getBaseSchema = (key) => {
    if (key.startsWith("hyrox-")) {
      const h = HYROX_TRAININGEN.find(t => t.id === key);
      if (!h) return null;
      return {
        sessions: [{
          naam: h.naam,
          exercises: h.exercises.map((ex, i) => ({
            code: String.fromCharCode(65 + i) + "1",
            naam: ex.naam,
            sets_reps: ex.doel_waarde || ex.sets || "",
            tempo: ex.type || "",
            rust: ex.rustTijd ? `${ex.rustTijd} sec` : "",
            notitie: ex.notitie || "",
          })),
        }],
        week2_sessions: null,
      };
    }
    return getSchema(key);
  };

  const selectSchema = async (key) => {
    setLoading(true);
    setSelectedKey(key);
    setActiveTab(0);
    setDirty(false);
    try {
      const base = getBaseSchema(key);
      const override = await api.getSchemaOverride(key);
      if (override.sessions?.length) {
        setEditSessions(JSON.parse(JSON.stringify(override.sessions)));
        setEditWeek2(override.week2_sessions ? JSON.parse(JSON.stringify(override.week2_sessions)) : null);
        setHasOverride(true);
      } else {
        setEditSessions(JSON.parse(JSON.stringify(base.sessions)));
        setEditWeek2(base.week2_sessions ? JSON.parse(JSON.stringify(base.week2_sessions)) : null);
        setHasOverride(false);
      }
    } catch { showToast("Laden mislukt", "error"); }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveSchemaOverride(selectedKey, editSessions, editWeek2);
      setHasOverride(true);
      setDirty(false);
      showToast("Schema opgeslagen!");
    } catch { showToast("Opslaan mislukt", "error"); }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm("Weet je zeker dat je de aanpassingen wilt verwijderen en terugzet naar de originele versie?")) return;
    try {
      await api.resetSchemaOverride(selectedKey);
      setHasOverride(false);
      const base = getBaseSchema(selectedKey);
      setEditSessions(JSON.parse(JSON.stringify(base.sessions)));
      setEditWeek2(base.week2_sessions ? JSON.parse(JSON.stringify(base.week2_sessions)) : null);
      setDirty(false);
      showToast("Schema teruggezet naar origineel");
    } catch { showToast("Terugzetten mislukt", "error"); }
  };

  const updateSession = (idx, updated, isWeek2 = false) => {
    setDirty(true);
    if (isWeek2) {
      const s = [...editWeek2]; s[idx] = updated; setEditWeek2(s);
    } else {
      const s = [...editSessions]; s[idx] = updated; setEditSessions(s);
    }
  };

  const allTabs = editSessions
    ? [
        ...editSessions.map((s, i) => ({ label: s.naam || `Sessie ${i + 1}`, isWeek2: false, idx: i })),
        ...(editWeek2 ? editWeek2.map((s, i) => ({ label: `Week 2 – ${s.naam || `Sessie ${i + 1}`}`, isWeek2: true, idx: i })) : []),
      ]
    : [];

  const filteredSchemas = SCHEMA_OPTIONS.filter(s =>
    !search || s.label.toLowerCase().includes(search.toLowerCase())
  );
  const groupedSchemas = ["vetverlies", "spieropbouw", "onderhoud", "powerlift", "hyrox"].map(doel => ({
    doel,
    label: DOEL_LABELS[doel],
    colors: DOEL_COLORS[doel],
    schemas: filteredSchemas.filter(s => s.doel === doel),
  }));

  return (
    <div className="flex gap-6 h-[calc(100vh-220px)] min-h-[500px]">
      <div className="w-64 shrink-0 flex flex-col gap-3">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Zoek schema..." className="course-input pl-9 text-sm" />
        </div>
        <div className="overflow-y-auto flex-1 space-y-4 pr-1">
          {groupedSchemas.map(({ doel, label, colors, schemas }) => schemas.length > 0 && (
            <div key={doel}>
              <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${colors.text}`}>{label}</p>
              <div className="space-y-1">
                {schemas.map(s => (
                  <button key={s.product} onClick={() => selectSchema(s.product)}
                    data-testid={`schema-edit-btn-${s.product}`}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                      selectedKey === s.product
                        ? `${colors.bg} ${colors.text} border ${colors.border} font-semibold`
                        : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`}>
                    {s.label.replace(`${label} `, "")}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        {!selectedKey ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <Dumbbell size={40} className="text-zinc-700 mb-4" />
            <p className="text-zinc-400 text-sm font-medium">Selecteer een schema links om te bewerken</p>
            <p className="text-zinc-600 text-xs mt-1">Klik op een schema in de lijst</p>
          </div>
        ) : loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-zinc-500 text-sm">Laden...</div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-white font-bold text-base">
                    {SCHEMA_OPTIONS.find(s => s.product === selectedKey)?.label}
                  </h2>
                  {hasOverride && (
                    <span className="text-xs bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/25 px-2 py-0.5 rounded-full font-semibold">Aangepast</span>
                  )}
                  {dirty && (
                    <span className="text-xs bg-amber-500/15 text-amber-400 border border-amber-500/25 px-2 py-0.5 rounded-full font-semibold">Niet opgeslagen</span>
                  )}
                </div>
                <p className="text-zinc-500 text-xs mt-0.5">{allTabs.length} sessie{allTabs.length !== 1 ? "s" : ""} · {editSessions?.reduce((a, s) => a + s.exercises.length, 0) || 0} oefeningen</p>
              </div>
              <div className="flex items-center gap-2">
                {hasOverride && (
                  <button onClick={handleReset} className="btn-course-outline text-xs px-3 py-2 flex items-center gap-1.5">
                    <RotateCcw size={12} /> Origineel herstellen
                  </button>
                )}
                <button onClick={handleSave} disabled={saving || !dirty} data-testid="save-schema-btn"
                  className="btn-gold-course px-4 py-2 text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 disabled:opacity-40">
                  <Save size={12} /> {saving ? "Opslaan..." : "Opslaan"}
                </button>
              </div>
            </div>

            <div className="flex gap-1 mb-4 flex-wrap">
              {allTabs.map((tab, i) => (
                <button key={tab.label} onClick={() => setActiveTab(i)}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                    activeTab === i ? "bg-[#C9A84C] text-black" : "bg-[#111] text-zinc-500 hover:text-white border border-white/5"
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto pr-1">
              {allTabs[activeTab] && (
                <SessionEditor
                  session={allTabs[activeTab].isWeek2
                    ? editWeek2[allTabs[activeTab].idx]
                    : editSessions[allTabs[activeTab].idx]}
                  onChange={upd => updateSession(allTabs[activeTab].idx, upd, allTabs[activeTab].isWeek2)}
                  token={token}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
